import * as React from 'react';
import { reactiveFeedbackMessage } from 'core/apollo-cache';
import { useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';
import {
  Button,
  Drawer,
  FieldLabel,
  Radio,
  Toggle,
} from '@teamsnap/teamsnap-ui';
import { DateService, FeatureFlagService } from 'frontend-toolkit';

// Local Imports
import './DiscountDrawerForm.scss';
import ENVIRONMENT from 'core/environment';
import MultiSelectPicker from 'frontend-toolkit/src/components/MultiSelectPicker/MultiSelectPicker';
import FormElementHook, {
  useFormDefaults,
} from 'components/form/form-element-hook';
import { convertToUTC } from '../date-utils';
import { convertToPennies } from 'core/utils';
import { FeatureFlagConfig, FeatureFlags } from 'core/feature-flags';
import {
  FormAdjustmentDefinition,
  RegistrationFormAdjustment,
} from '../AddOnFees/models';
import * as CreateFormAdjustmentDefinition from 'core/gql/mutations/create-form-adjustment-defnition';
import * as UpdateFormAdjustmentDefinition from 'core/gql/mutations/update-form-adjustment-definition';
import * as CreateFormAdjustment from 'core/gql/mutations/create-form-adjustment';
import * as UpdateFormAdjustment from 'core/gql/mutations/update-form-adjustment';
import * as RegistrationFormAdjustments from 'core/gql/queries/registration-form-adjustments';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  formId: number;
  organization: any;
  currency: string;
  currencySymbol: string;
  discount?: RegistrationFormAdjustment;
  discounts: RegistrationFormAdjustment[];
  currentDiscounts?: RegistrationFormAdjustment[];
  determineDateFromTimezone: (date: string) => Date;
};

const CreateElement = FormElementHook as new () => FormElementHook;

const DISCOUNT_TYPE_OPTIONS = [
  {
    text: 'Percentage (%)',
    value: 'percentage',
  },
  {
    text: 'Custom Amount',
    value: 'custom_amount',
  },
];

const QUALIFYING_NUMBER_OF_OPTIONS_TYPE_OPTIONS = [
  {
    text: 'Equal to',
    value: 'equal_to',
  },
  {
    text: 'Greater than',
    value: 'greater_than',
  },
];

const APPLIES_TO_OPTIONS = ['Registration option fee(s)', 'Add on fee(s)'];

const showSuccess = (message) => {
  reactiveFeedbackMessage({
    message: message,
    isError: false,
    autoclose: true,
  });
};

const showError = (message) => {
  reactiveFeedbackMessage({
    message: message,
    isError: true,
    autoclose: false,
  });
};

const DiscountDrawerForm: React.FC<Props> = ({
  isOpen,
  onClose,
  formId,
  discount,
  discounts,
  organization,
  currency,
  currencySymbol,
  currentDiscounts,
  determineDateFromTimezone,
}) => {
  const orgId = organization?.id;
  const timezoneIana = organization?.timezoneIana;

  // TODO: Remove Feature Flag once feature is fully released.
  const automaticDiscountsEnabled = FeatureFlagService.isFeatureEnabledForOrg(
    FeatureFlagConfig[FeatureFlags.AUTOMATIC_DISCOUNTS],
    ENVIRONMENT,
    orgId || undefined
  );

  const [selectedAppliesToOptions, setSelectedAppliesToOptions] =
    React.useState<string[]>([APPLIES_TO_OPTIONS[0]]);
  const [
    qualifyingNumberOfOptionsIsEnabled,
    setQualifyingNumberOfOptionsIsEnabled,
  ] = React.useState<boolean>(false);
  const [dateBasedValidityIsEnabled, setDateBasedValidityIsEnabled] =
    React.useState<boolean>(false);
  const [isAutomaticDiscount, setIsAutomaticDiscount] =
    React.useState<boolean>(true);
  const [showStartDateError, setShowStartDateError] =
    React.useState<boolean>(false);
  const [showEndDateError, setShowEndDateError] =
    React.useState<boolean>(false);
  const [showAppliesToError, setShowAppliesToError] =
    React.useState<boolean>(false);
  const [showDupicateCouponCodeError, setShowDupicateCouponCodeError] =
    React.useState<boolean>(false);

  const discountForm = useForm();

  const { setValue, register, handleSubmit } = discountForm;
  const discountType = discountForm.watch('discountType');
  const couponCode = discountForm.watch('couponCode');
  const qualifyingOperator =
    discountForm.watch('qualifyingOperator') || 'equal_to';
  const startDateValue = discountForm.watch('startDate') || discount?.startDate;
  const endDateValue = discountForm.watch('endDate') || discount?.endDate;

  const refetch = {
    refetchQueries: [
      {
        query: RegistrationFormAdjustments.QUERY,
        variables: { formId: +formId },
      },
    ],
  };

  const [
    createFormAdjustmentDefinition,
    createFormAdjustmentDefinitionResponse,
  ] = useMutation(CreateFormAdjustmentDefinition.MUTATION);

  const [
    updateFormAdjustmentDefinition,
    updateFormAdjustmentDefinitionResponse,
  ] = useMutation(UpdateFormAdjustmentDefinition.MUTATION);

  const [createFormAdjustment, createFormAdjustmentResponse] = useMutation(
    CreateFormAdjustment.MUTATION,
    refetch
  );
  const [updateFormAdjustment, updateFormAdjustmentResponse] = useMutation(
    UpdateFormAdjustment.MUTATION,
    refetch
  );

  const isSubmitting =
    createFormAdjustmentDefinitionResponse.loading ||
    updateFormAdjustmentDefinitionResponse.loading ||
    createFormAdjustmentResponse.loading ||
    updateFormAdjustmentResponse.loading;

  const determineAppliesToValue = () => {
    if (selectedAppliesToOptions.length === APPLIES_TO_OPTIONS.length) {
      return 'all';
    } else if (selectedAppliesToOptions.includes(APPLIES_TO_OPTIONS[0])) {
      return 'registration_fee';
    } else if (selectedAppliesToOptions.includes(APPLIES_TO_OPTIONS[1])) {
      return 'fees';
    } else {
      return undefined;
    }
  };

  const determineDiscountTypeDisplay = (discount) => {
    if (discount && discount.percentage) {
      return 'percentage';
    } else if (discount && !discount.percentage && discount.amount) {
      return 'custom_amount';
    } else {
      return 'percentage';
    }
  };

  const setupFormDefaults = React.useCallback(
    (data, reset = false) => {
      if (reset) {
        data = {};

        setShowStartDateError(false);
        setShowEndDateError(false);
      }

      if (data?.qualifyingOperator && data?.qualifyingCount) {
        setQualifyingNumberOfOptionsIsEnabled(true);
      } else {
        setQualifyingNumberOfOptionsIsEnabled(false);
      }

      if (data?.startDate && data?.endDate) {
        setDateBasedValidityIsEnabled(true);
      } else {
        setDateBasedValidityIsEnabled(false);
      }

      if (data?.qualifyingOperator && data?.qualifyingCount) {
        setQualifyingNumberOfOptionsIsEnabled(true);
      } else {
        setQualifyingNumberOfOptionsIsEnabled(false);
      }

      if (data?.couponCode && data?.appliesTo) {
        setIsAutomaticDiscount(false);
      } else {
        setIsAutomaticDiscount(true);
      }

      Object.entries({
        discountName: data?.formAdjustmentDefinition?.name ?? '',
        discountType: determineDiscountTypeDisplay(data),
        discountAmount:
          data?.percentage !== null
            ? data?.percentage / 100
            : (data?.amount / 100).toFixed(2) ?? '',
        couponCode: data?.couponCode ?? '',
        qualifyingOperator: data?.qualifyingOperator ?? 'equal_to',
        qualifyingCount: data?.qualifyingCount ?? '',
        startDate: data?.startDate
          ? determineDateFromTimezone(data.startDate)
          : '',
        endDate: data?.endDate
          ? DateService.removeDaysFromDate(
              1,
              determineDateFromTimezone(data.endDate)
            )
          : '',
      }).forEach(([k, v]) => setValue(k, v));

      if (data?.appliesTo === 'all') {
        setSelectedAppliesToOptions(APPLIES_TO_OPTIONS);
      } else if (data?.appliesTo === 'registration_fee') {
        setSelectedAppliesToOptions([APPLIES_TO_OPTIONS[0]]);
      } else if (data?.appliesTo === 'fees') {
        setSelectedAppliesToOptions([APPLIES_TO_OPTIONS[1]]);
      } else {
        setSelectedAppliesToOptions([APPLIES_TO_OPTIONS[0]]);
      }
    },
    [determineDateFromTimezone, setValue]
  );

  useFormDefaults(discount, setupFormDefaults, [isOpen]);

  const handleQualifyingNumberOfOptionsCheckbox = () => {
    setQualifyingNumberOfOptionsIsEnabled(!qualifyingNumberOfOptionsIsEnabled);
  };

  const handleDataBasedValidityCheckbox = () => {
    setDateBasedValidityIsEnabled(!dateBasedValidityIsEnabled);
  };

  const handleClose = () => {
    setupFormDefaults(discount, true);
    discountForm.clearError();
    onClose();
  };

  const handleAppliesToUpdate = (selected: string[]) => {
    if (selected.length === 0) {
      setShowAppliesToError(true);
    } else {
      setShowAppliesToError(false);
    }

    setSelectedAppliesToOptions(selected);
  };

  const saveOrCreateDiscount = async (values) => {
    if (selectedAppliesToOptions.length === 0) {
      setShowAppliesToError(true);
    }

    const validEndDate = DateService.compareDates(
      new Date(endDateValue),
      new Date(startDateValue),
      0,
      'greater_or_equal'
    );

    if (dateBasedValidityIsEnabled && (!validEndDate || !endDateValue)) {
      setShowEndDateError(true);
    } else {
      setShowEndDateError(false);
    }

    if (dateBasedValidityIsEnabled && !startDateValue) {
      setShowStartDateError(true);
    } else {
      setShowStartDateError(false);
    }

    if (
      selectedAppliesToOptions.length === 0 ||
      (dateBasedValidityIsEnabled &&
        (!startDateValue || !endDateValue || !validEndDate))
    ) {
      return;
    }

    let formAdjustmentDefinition: FormAdjustmentDefinition | undefined;

    // Reuse form adjustment definition if it exists.
    if (discount && discount.formAdjustmentDefinition?.id) {
      formAdjustmentDefinition = discount.formAdjustmentDefinition;
    }

    if (!discount && discounts) {
      formAdjustmentDefinition = discounts.find(
        (d) =>
          d.formAdjustmentDefinition?.name?.trim() ===
          values.discountName?.trim()
      )?.formAdjustmentDefinition;
    }

    let formAdjustmentDefinitionPayload: any = {
      organizationId: orgId,
      name: values.discountName,
      category: 'Discount',
      amount:
        values.discountType === 'percentage'
          ? undefined
          : +convertToPennies(values.discountAmount),
    };

    const startDate = convertToUTC(
      values.startDate?.toLocaleString('en-US'),
      timezoneIana
    );

    const endDate = convertToUTC(
      DateService.addDaysToDate(1, values?.endDate)?.toLocaleString('en-US'),
      timezoneIana
    );

    let formAdjustmentPayload: any = {
      formId: +formId,
      formFieldId: undefined,
      formAdjustmentDefinitionId: formAdjustmentDefinition?.id,
      answerToMatch: undefined,
      maxQuantity: 1,
      amount:
        values.discountType === 'percentage'
          ? Math.round(parseFloat(values.discountAmount) * 100)
          : convertToPennies(values.discountAmount),
      amountType: values.discountType,
      couponCode: isAutomaticDiscount ? null : values.couponCode,
      appliesTo: determineAppliesToValue(),
      enabled: true,
      qualifyingOperator: qualifyingNumberOfOptionsIsEnabled
        ? values.qualifyingOperator
        : null,
      qualifyingCount: qualifyingNumberOfOptionsIsEnabled
        ? +values.qualifyingCount
        : null,
      startDate: dateBasedValidityIsEnabled ? startDate : null,
      endDate: dateBasedValidityIsEnabled ? endDate : null,
    };

    if (!discount) {
      try {
        if (!formAdjustmentDefinition) {
          formAdjustmentDefinitionPayload = {
            ...formAdjustmentDefinitionPayload,
            type: 'discount',
          };

          formAdjustmentDefinition = (
            await createFormAdjustmentDefinition({
              variables: formAdjustmentDefinitionPayload,
            })
          ).data?.createFormAdjustmentDefinition;
        }

        formAdjustmentPayload = {
          ...formAdjustmentPayload,
          formAdjustmentDefinitionId: formAdjustmentDefinition?.id,
        };

        await createFormAdjustment({ variables: formAdjustmentPayload });

        handleClose();
        showSuccess('Discount created successfully');
      } catch (error) {
        console.log('Error creating discount: ', error);
        showError(
          'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.'
        );
      }
    } else if (discount && discount.id) {
      try {
        const formAdjustmentDefinitionContainsMoreThanOneFormAdjustment =
          discounts.filter(
            (discount) =>
              discount.formAdjustmentDefinition?.id ===
              formAdjustmentDefinition.id
          ).length > 1;

        const formsAdjustmentDefinitionNameHasChanged =
          formAdjustmentDefinition.name.trim() !== values.discountName.trim();

        if (
          formsAdjustmentDefinitionNameHasChanged &&
          !formAdjustmentDefinitionContainsMoreThanOneFormAdjustment
        ) {
          formAdjustmentDefinitionPayload = {
            id: formAdjustmentDefinition.id,
            ...formAdjustmentDefinitionPayload,
          };

          formAdjustmentDefinition = (
            await updateFormAdjustmentDefinition({
              variables: formAdjustmentDefinitionPayload,
            })
          ).data?.updateFormAdjustmentDefinition;
        } else if (
          formsAdjustmentDefinitionNameHasChanged &&
          formAdjustmentDefinitionContainsMoreThanOneFormAdjustment
        ) {
          formAdjustmentDefinitionPayload = {
            ...formAdjustmentDefinitionPayload,
            type: 'discount',
          };

          formAdjustmentDefinition = (
            await createFormAdjustmentDefinition({
              variables: formAdjustmentDefinitionPayload,
            })
          ).data?.createFormAdjustmentDefinition;
        }

        formAdjustmentPayload = {
          ...formAdjustmentPayload,
          id: discount.id,
          formAdjustmentDefinitionId: formAdjustmentDefinition?.id,
        };

        await updateFormAdjustment({ variables: formAdjustmentPayload });

        handleClose();
        showSuccess('Discount updated successfully');
      } catch (error) {
        console.error('Error updating discount: ', error);
        showError('Oops! Something went wrong. Please try again.');
      }
    }
  };

  return (
    <Drawer
      open={isOpen}
      allowOverlayClose={false}
      style={{ maxWidth: '730px' }}
      closeFn={handleClose}
      mods="u-sizeFull"
      showOverlay
      allowBodyScroll={false}
    >
      <div
        className="Discounts__form sui-px-2 sui-flex sui-flex-col"
        id="discounts-drawer"
        data-testid="discounts-drawer-id"
      >
        <div className="sui-py-2 sui-flex sui-align-items sui-justify-between">
          <h3
            data-testid="discount-drawer-form--heading"
            className="discount-heading"
          >
            {discount
              ? `Edit ${discount.formAdjustmentDefinition?.name}`
              : 'Create Discount'}
          </h3>

          <Button
            type="link"
            icon="dismiss"
            mods="u-colorNeutral8"
            onClick={handleClose}
          />
        </div>

        <form>
          <div
            className="sui-px-4 sui-py-4"
            style={{ borderRadius: 5, border: '1px solid #DCE1E7' }}
          >
            <h3 className="sui-mb-2">Attributes</h3>

            <div className="sui-mb-1">
              <FieldLabel name="discountName">
                Discount Name<span className="sui-text-red-40">*</span>
              </FieldLabel>
            </div>

            <div className="Discounts__field-input-col">
              <CreateElement
                disabled={isSubmitting}
                inputRef={register({
                  required: 'You must enter a name for this discount',
                })}
                useHookResponse={discountForm}
                maxLength={100}
                type="text"
                labelHidden
                field="discountName"
                label="Discount Name"
                placeholder="e.g. Returning Player Discount"
              />

              <p className="helper sui-text-gray-40">
                The discount name is displayed in the registration cart. (e.g.
                Returning Player Discount, etc.)
              </p>
            </div>

            <div className="sui-mt-2 sui-mb-1">
              <FieldLabel name="discountAmount">
                Discount Amount<span className="sui-text-red-40">*</span>
              </FieldLabel>
            </div>

            <div className="sui-flex Discounts__field-input-col">
              <CreateElement
                disabled={isSubmitting}
                inputRef={register({})}
                useHookResponse={discountForm}
                type="select"
                options={DISCOUNT_TYPE_OPTIONS}
                labelHidden
                label="Discount Type"
                field="discountType"
                className="sui-mr-2"
              />

              {discountType === 'custom_amount' && (
                <div className="Discounts__input-compound">
                  <span className="field-prefix">{currencySymbol}</span>
                  <CreateElement
                    disabled={isSubmitting}
                    inputRef={register({
                      required: 'You must enter an amount greater than $0',
                      min: 0.01,
                    })}
                    useHookResponse={discountForm}
                    type="number"
                    labelHidden
                    field="discountAmount"
                    placeholder="0.00"
                    numberInputAttributes={{ min: 0.01, step: 0.01 }}
                  />

                  <span className="field-suffix">{currency}</span>
                </div>
              )}

              {discountType === 'percentage' && (
                <div className="Discounts__input-compound">
                  <CreateElement
                    disabled={isSubmitting}
                    inputRef={register({
                      required:
                        'You must enter a discount percentage between 1 and 100',
                      min: 1,
                      max: 100,
                    })}
                    useHookResponse={discountForm}
                    type="number"
                    labelHidden
                    field="discountAmount"
                    placeholder="0"
                    numberInputAttributes={{ min: 1, step: 1, max: 100 }}
                  />

                  <span className="field-suffix">%</span>
                </div>
              )}
            </div>
          </div>

          <div
            className="sui-p-4 sui-mt-2"
            style={{ borderRadius: 5, border: '1px solid #DCE1E7' }}
          >
            <h3 className="sui-mb-2">Rules</h3>
            <div className="sui-bg-gray-40/10 sui-rounded sui-py-2 sui-px-1">
              <div className="sui-mb-1">
                <FieldLabel name="appliesTo">
                  Applies to<span className="sui-text-red-40">*</span>
                </FieldLabel>
              </div>

              <div className="Discounts__field-input-col">
                <MultiSelectPicker
                  testId="discount-drawer-form--applies-to-select"
                  options={APPLIES_TO_OPTIONS.map((x) => ({
                    label: x,
                    value: x,
                  }))}
                  updateSelectedOptions={handleAppliesToUpdate}
                  selected={selectedAppliesToOptions}
                  containerMods={
                    showAppliesToError
                      ? 'DiscountDrawerForm--applies-to-select u-border sui-rounded sui-border-red-40'
                      : 'DiscountDrawerForm--applies-to-select'
                  }
                />

                {showAppliesToError && (
                  <p className="validation-error">
                    You must select at least one fee option
                  </p>
                )}
              </div>
            </div>

            <div className="sui-bg-gray-40/10 sui-rounded sui-pt-2 sui-px-1 sui-mt-2">
              {automaticDiscountsEnabled && (
                <div className="Discounts__field-input-col">
                  <div className="sui-block">
                    <Radio
                      disabled={isSubmitting}
                      name="isAutomatic"
                      label="Apply automatically"
                      group="discountCategory"
                      id="isAutomatic"
                      inputProps={{
                        onChange: () => {
                          setIsAutomaticDiscount(true);
                          setValue('couponCode', null);
                        },
                        checked: isAutomaticDiscount,
                      }}
                    />

                    <Radio
                      disabled={isSubmitting}
                      name="isNotAutomatic"
                      label="Require coupon code"
                      group="discountCategory"
                      id="isNotAutomatic"
                      inputProps={{
                        onChange: () => setIsAutomaticDiscount(false),
                        checked: !isAutomaticDiscount,
                      }}
                    />
                  </div>
                </div>
              )}

              {!isAutomaticDiscount && (
                <div className="Discounts__field-input-col sui-pb-2">
                  <div className="sui-mb-1">
                    <FieldLabel name="couponCode">
                      Coupon Code<span className="sui-text-red-40">*</span>
                    </FieldLabel>
                  </div>

                  <div className="Discounts__field-input-col">
                    <CreateElement
                      disabled={isSubmitting}
                      inputRef={register({
                        required:
                          'You must enter a coupon code between 3 and 30 characters in length. Spaces and special characters are not allowed',
                        min: 3,
                        max: 30,
                        validate: (value) =>
                          (value.length > 2 &&
                            value.length < 31 &&
                            /^[a-zA-Z0-9]+$/.test(value)) ||
                          'You must enter a coupon code between 3 and 30 characters in length. Spaces and special characters are not allowed',
                      })}
                      onChange={(e) => {
                        setShowDupicateCouponCodeError(false);
                        setValue(
                          'couponCode',
                          e.target.value.toUpperCase().replace(/[^\w\s]/gi, '')
                        );
                      }}
                      useHookResponse={discountForm}
                      maxLength={30}
                      type="text"
                      labelHidden
                      field="couponCode"
                      label="Coupon Code"
                      placeholder="e.g. 20OFF"
                    />
                    {showDupicateCouponCodeError && (
                      <p className="validation-error">
                        This coupon code is already in use. You must enter a
                        different code.
                      </p>
                    )}
                    <p className="helper sui-text-gray-40">
                      Coupon codes must be 3 to 30 characters in length, using
                      letters and/or numbers. Special characters are not
                      allowed.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {automaticDiscountsEnabled && (
              <div className="sui-bg-gray-40/10 sui-rounded sui-py-2 sui-px-1 sui-mt-2">
                <div className="Discounts__field-input-col sui-mb-2">
                  <div className="sui-flex">
                    <Toggle
                      name="dateBasedValidityIsEnabled"
                      style={{ color: 'green' }}
                      testId="discount-drawer-form--date-based-validity-checkbox"
                      inputProps={{
                        checked: dateBasedValidityIsEnabled,
                        onChange: () => {},
                        onClick: handleDataBasedValidityCheckbox,
                      }}
                    />

                    <FieldLabel
                      name="dateBasedValidityIsEnabled"
                      className="sui-ml-2"
                    >
                      Date-based validity
                    </FieldLabel>
                  </div>

                  {dateBasedValidityIsEnabled && (
                    <div className="DiscountDrawerFormDatePickerContainer sui-flex sui-mt-1 sui-items-baseline">
                      <div className="sui-mr-2 sui-w-2/5">
                        <CreateElement
                          disabled={isSubmitting}
                          inputRef={register()}
                          useHookResponse={discountForm}
                          type="date"
                          labelHidden
                          label=""
                          field="startDate"
                          className={`discount-drawer-form--start-date-input sui-mb-0 ${
                            showStartDateError
                              ? 'u-border sui-rounded sui-border-red-40'
                              : ''
                          }`}
                          placeholder="mm/dd/yyyy"
                        />

                        {showStartDateError && (
                          <p className="validation-error">
                            You must enter a valid date
                          </p>
                        )}
                      </div>

                      <p>to</p>

                      <div className="sui-ml-2 sui-w-2/5">
                        <CreateElement
                          disabled={isSubmitting}
                          inputRef={register()}
                          useHookResponse={discountForm}
                          type="date"
                          labelHidden
                          label=""
                          field="endDate"
                          className={`discount-drawer-form--end-date-input  sui-mb-0 ${
                            showEndDateError
                              ? 'u-border sui-rounded sui-border-red-40'
                              : ''
                          }`}
                          placeholder="mm/dd/yyyy"
                        />

                        {showEndDateError && (
                          <p className="validation-error">
                            You must enter a valid date
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="Discounts__field-input-col">
                  <div className="sui-flex">
                    <Toggle
                      name="qualifyingNumberOfOptionsEnabled"
                      style={{ color: 'green' }}
                      testId="discount-drawer-form--qualifying-number-of-options-checkbox"
                      inputProps={{
                        checked: qualifyingNumberOfOptionsIsEnabled,
                        onChange: () => {},
                        onClick: handleQualifyingNumberOfOptionsCheckbox,
                      }}
                    />

                    <FieldLabel
                      name="qualifyingNumberOfOptionsEnabled"
                      className="sui-ml-2"
                    >
                      Qualifying # of options in cart
                    </FieldLabel>
                  </div>

                  {qualifyingNumberOfOptionsIsEnabled && (
                    <div className="sui-flex sui-mt-1">
                      <CreateElement
                        disabled={isSubmitting}
                        inputRef={register({})}
                        useHookResponse={discountForm}
                        type="select"
                        options={QUALIFYING_NUMBER_OF_OPTIONS_TYPE_OPTIONS}
                        labelHidden
                        label=""
                        field="qualifyingOperator"
                        className="sui-mr-2"
                      />

                      <span className="sui-w-1/2">
                        <CreateElement
                          disabled={isSubmitting}
                          inputRef={register({
                            required: 'You must enter a number to proceed',
                            min: 0,
                            max: 999999,
                            validate: (value) =>
                              (qualifyingOperator === 'equal_to' &&
                                value > 0) ||
                              'You must enter a number greater than 0',
                          })}
                          numberInputAttributes={{
                            min: 0,
                            step: 1,
                            max: 6,
                          }}
                          useHookResponse={discountForm}
                          type="number"
                          labelHidden
                          field="qualifyingCount"
                          placeholder="e.g. 2"
                          onKeyDown={(e) => {
                            if (e.key === '.') {
                              e.preventDefault();
                            }
                          }}
                        />
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </form>

        <div className="sui-flex sui-justify-end sui-px-2 sui-py-2 ">
          <div className="button-list">
            <Button
              testId="discount-drawer-form--cancel"
              isDisabled={isSubmitting}
              onClick={handleClose}
            >
              Cancel
            </Button>

            <Button
              testId="discount-drawer-form--save"
              isDisabled={isSubmitting}
              type="submit"
              color="primary"
              onClick={(e) => {
                e.preventDefault();
                setShowDupicateCouponCodeError(false);
                const foundCode = currentDiscounts?.find(
                  (d) => d.couponCode === couponCode
                );
                if (foundCode && foundCode.id !== discount?.id) {
                  setShowDupicateCouponCodeError(true);
                } else {
                  handleSubmit(saveOrCreateDiscount)();
                }
              }}
            >
              {discount?.id ? 'Save' : 'Create'}
            </Button>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

DiscountDrawerForm.defaultProps = {
  discount: undefined,
  currentDiscounts: undefined,
};

export default DiscountDrawerForm;
